"use client"

import { motion } from "@/components/motion/framer"

interface ContactFormProps {
  className?: string
}

export function ContactForm({ className }: ContactFormProps) {
  const isSmall = className?.includes('h-32') || className?.includes('h-24') || className?.includes('h-20')
  const scaleClass = isSmall ? '' : 'scale-150 sm:scale-[2.5]'
  
  return (
    <div className={`relative h-full w-full ${className || ""}`}>
      <div className={`absolute inset-0 flex items-center justify-center gap-4 ${scaleClass}`}>
        {/* Message/Form - left side */}
        <motion.div
          className="relative h-24 w-20 rounded border-2 border-tk-slate/40 bg-tk-linen/80 shadow-sm"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {/* Form structure */}
          <div className="flex h-full w-full flex-col gap-1 p-2">
            {/* Header/Title */}
            <div className="h-2 w-full rounded border border-tk-slate/30 bg-tk-slate/5" />
            
            {/* Input fields */}
            <div className="flex-1 flex flex-col gap-0.5">
              <div className="h-1.5 w-full rounded border border-tk-slate/30" />
              <div className="h-1.5 w-full rounded border border-tk-slate/30" />
              <div className="h-3 w-full rounded border border-tk-slate/30" />
            </div>
            
            {/* Submit button */}
            <motion.div
              className="h-2 w-2/3 rounded bg-tk-teal/30 border border-tk-teal/50 ml-auto"
              animate={{ opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>

        {/* Arrow pointing right */}
        <motion.div
          className="absolute -right-3 top-1/2 -translate-y-1/2 text-tk-slate/40"
          animate={{ x: [0, 3, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          →
        </motion.div>

        {/* Connection/Response - right side */}
        <motion.div
          className="relative h-24 w-20 rounded border-2 border-tk-teal/40 bg-tk-teal/10"
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2, delay: 0.3, repeat: Infinity }}
        >
          {/* Success/Response indicators */}
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2">
            {/* Checkmark or success indicator */}
            <motion.div
              className="relative"
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.2, 1] }}
              transition={{ duration: 0.6, delay: 0.5, repeat: Infinity, repeatDelay: 2 }}
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <motion.path
                  d="M 5 12 L 10 17 L 20 7"
                  stroke="#009F9A"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.5, delay: 0.5, repeat: Infinity, repeatDelay: 2 }}
                />
              </svg>
            </motion.div>
            
            {/* Connection lines */}
            <div className="flex gap-1">
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="h-0.5 w-2 rounded bg-tk-teal/40"
                  animate={{ opacity: [0.3, 0.8, 0.3] }}
                  transition={{ duration: 1, delay: i * 0.2, repeat: Infinity }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

